import { useCallback, useEffect, useState } from 'react';
import {
  AlertBanner,
  Button,
  Checkbox,
  Dialog,
  EmptyState,
  ErrorState,
  LoadingState,
} from '@assist/ui';
import { renderProblem } from '@assist/i18n';
import { endpoints, ApiError, type ConsentsResponse } from '../state/api.js';
import { useSession } from '../state/session.js';

/**
 * PRIVASI & DATA ANDA — M01 · BAGIAN dari `/settings/account`, rute
 * `/settings/account/privacy`. Acuan Figma `188:1225` (EN) / `190:1914` (ID).
 *
 * IA BUKAN LAYAR TERSENDIRI, DAN ITU KEPUTUSAN PERANCANG YANG PERLU DIBACA
 * ----------------------------------------------------------------------
 * Seseorang yang bertanya "apa yang sistem ini tahu tentang saya" tidak membedakan
 * pertanyaan itu dari "bahasa apa yang saya pakai", "perangkat mana yang masih login",
 * dan "notifikasi apa yang saya terima". Keempatnya satu tempat di kepala orang: AKUN
 * SAYA. Rute `/settings/privacy` yang berdiri sendiri menciptakan tempat kedua untuk satu
 * pertanyaan, dan peta §4 sudah dengan sengaja menolak memberi pengaturan akun slot
 * navigasi sendiri.
 *
 * KOREKSINYA, dan inilah yang membedakannya dari "digabungkan" begitu saja:
 * menggabungkan BUKAN berarti memerasnya menjadi satu panel di rel kanan. Isi nyatanya
 * empat — versi pemberitahuan yang dibaca, pengakuan yang tidak dapat ditarik, izin yang
 * dapat ditarik, dan RIWAYAT append-only — dan riwayat itulah yang paling ingin diperiksa
 * subjek data, karena di situlah terlihat kapan ia berubah pikiran. Yang ada di
 * `/settings/account` hanyalah panel RINGKAS dengan tautan `Kelola` ke sini.
 *
 * `/settings/privacy` menjadi PENGALIHAN permanen — tabel `PENGALIHAN` di `App.tsx`.
 *
 * Tiga hal yang harus ada di satu tempat, dan ini tempatnya:
 *
 *   1. apa yang sudah disetujui orang itu, dan versi pemberitahuan mana yang
 *      dibacanya ketika menyetujuinya;
 *   2. RIWAYAT lengkapnya — termasuk persetujuan yang sudah ditarik. Tabelnya
 *      append-only, dan menampilkan hanya keadaan terakhir akan menyembunyikan
 *      justru hal yang paling ingin diperiksa subjek data: kapan ia berubah
 *      pikiran;
 *   3. jalan untuk menarik apa yang MEMANG dapat ditarik, dengan akibatnya
 *      DINYATAKAN LEBIH DULU — bukan ditemukan sesudahnya.
 *
 * APA YANG DAPAT DITARIK, DAN APA YANG TIDAK
 * ------------------------------------------
 * Hari ini hanya foto profil berdiri di atas izin, dan ia benar-benar dapat ditolak:
 * tanpa foto, inisial yang ditampilkan, dan tidak ada bagian pekerjaan yang
 * terpengaruh.
 *
 * Baris `NOTICE_ACKNOWLEDGED` adalah PENGAKUAN telah diberi tahu (Pasal 21), dan ia
 * tidak dapat ditarik — seseorang tidak dapat menjadi belum-pernah diberi tahu.
 * Layar ini mengatakannya apa adanya alih-alih menyembunyikan barisnya, karena
 * menyembunyikannya akan membuat orang mengira ada sesuatu yang ia setujui tanpa
 * pernah melihatnya. Aksesnya pun tidak pernah bergantung pada centang itu: bentuk
 * sebelumnya mematikan akun orang yang menariknya, dan itu menghukum orang karena
 * menggunakan haknya atas pemrosesan yang tidak pernah bergantung pada izinnya.
 */
export interface PrivacySettingsProps {
  /**
   * Breadcrumb `‹ Account & notifications`. Ia wajib ada dalam pemakaian normal: tanpa
   * jalan pulih ke induknya, sebuah BAGIAN terasa seperti layar yang tersesat. Ia
   * opsional hanya supaya uji render tanpa DOM tidak perlu mengarang penangan navigasi.
   */
  onBack?: () => void;
}

export function PrivacySettings({ onBack }: PrivacySettingsProps) {
  const { t, translator, locale, refresh, me } = useSession();
  /**
   * NAMA organisasinya — UAT U2-04, bentuk keduanya.
   *
   * Layar ini mengirim `{ organisation }`, sebuah string kosong yang ditulis tetap di
   * kode, sehingga kalimatnya menjadi *"Saya mengerti bahwa **yang menentukan** bagaimana
   * data pribadi saya dipakai di sini…"* — tanpa subjek. Berbeda dengan layar terima
   * undangan, di sini namanya SUDAH ada di sesi: `me.tenant.name` dirender di bilah atas
   * konsol pada layar yang sama. Ia hanya tidak dipakai.
   *
   * Pengguna internal PQM tidak punya tenant; bagi mereka kalimat netral dari katalog
   * yang dipakai, bukan string kosong.
   */
  const organisation = me?.tenant?.name ?? t('accept.controllerUnknown');
  const [data, setData] = useState<ConsentsResponse | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [problem, setProblem] = useState<ApiError | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    endpoints
      .consents()
      .then(setData)
      .catch((err: unknown) => setFailed(err instanceof ApiError ? err.problem.code : 'INTERNAL'));
  }, []);

  useEffect(load, [load]);

  async function grant(purpose: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.recordConsent({ purpose, granted: true });
      load();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function withdraw(purpose: string) {
    setBusy(true);
    setProblem(null);
    try {
      await endpoints.withdrawConsent(purpose);
      setConfirming(null);
      /**
       * Tidak ada lagi cabang "sesinya sendiri dicabut". Satu-satunya penarikan yang
       * mungkin sekarang adalah foto profil, dan ia tidak menyentuh sesi siapa pun —
       * yang berubah hanya apa yang ditampilkan di samping nama. Memanggil `refresh()`
       * tetap dibutuhkan supaya avatar di bilah atas ikut hilang seketika, bukan pada
       * muat halaman berikutnya.
       */
      await refresh();
      load();
    } catch (err) {
      setProblem(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  if (failed) {
    return (
      <ErrorState
        title={t('state.error.title')}
        body={t('state.error.body')}
        code={failed}
        retryLabel={t('app.retry')}
        onRetry={load}
      />
    );
  }
  if (!data) return <LoadingState rows={5} label={t('app.loading')} />;

  const confirmingRow = data.state.find((s) => s.purpose === confirming) ?? null;

  return (
    <section>
      {onBack ? (
        <p className="report__crumb">
          <button type="button" className="link" onClick={onBack}>
            {t('consentSettings.backToAccount')}
          </button>
        </p>
      ) : null}
      <h1>{t('consentSettings.title')}</h1>
      <p>{t('consentSettings.subtitle')}</p>

      {problem ? (
        <AlertBanner
          kind="danger"
          signalWord={t('signal.danger')}
          title={renderProblem(translator, problem.problem)}
        />
      ) : null}

      <ul className="assist-consent-list">
        {data.state.map((row) => (
          <li className="assist-consent-list__item" key={row.purpose}>
            {/*
              PENGAKUAN dan IZIN DIPISAHKAN SECARA VISUAL, dan itu instruksi perancang.
              Pengakuan memakai `Checkbox` State=Locked (`163:14`): kotak TERCENTANG
              berwarna `ink/50` tanpa kendali sama sekali. Varian itu ada supaya tidak ada
              yang tergoda memakai kotak centang NONAKTIF — kotak nonaktif membuat orang
              mengira haknya sedang ditahan, sementara yang sebenarnya terjadi adalah
              seseorang tidak dapat menjadi belum-pernah diberi tahu (Pasal 21).
              Sebelum perbaikan ini, layar ini memakai `ConsentCheckbox … disabled` untuk
              SETIAP baris — termasuk izin yang benar-benar dapat ditarik — sehingga
              satu-satunya hal yang sungguh dapat ditarik pun terlihat seperti ditahan.
            */}
            {row.kind === 'ACKNOWLEDGEMENT' ? (
              <Checkbox
                state="locked"
                label={t(`privacy.consent.${row.purpose}.label`, { organisation })}
                hint={t('privacy.withdrawEffect.ACKNOWLEDGEMENT_CANNOT_BE_WITHDRAWN')}
              />
            ) : (
              <>
                {/* IZIN: tidak ada kotak centang sama sekali. Kendalinya adalah tombol
                    di bawah, dan akibatnya DINYATAKAN LEBIH DULU — bukan ditemukan
                    sesudah orang menekannya. Dua kendali untuk satu hal (kotak DAN
                    tombol) akan membuat salah satunya berbohong. */}
                <p className="assist-consent-list__label">
                  {t(`privacy.consent.${row.purpose}.label`, { organisation })}
                </p>
                <p className="card__muted">
                  {t(`privacy.withdrawEffect.${row.withdrawal_effect}`)}
                </p>
              </>
            )}
            {row.granted && row.kind !== 'ACKNOWLEDGEMENT' ? (
              <Button
                variant="secondary"
                onClick={() => setConfirming(row.purpose)}
                disabled={busy}
              >
                {t('consentSettings.withdraw')}
              </Button>
            ) : row.kind === 'ACKNOWLEDGEMENT' ? (
              /*
               * Tidak ada tombol di baris pengakuan, dan tidak ada tombol yang mati.
               * Kalimat alasannya sudah menjadi `hint` kotak terkuncinya di atas, jadi ia
               * TIDAK diulang di sini: dua salinan satu kalimat adalah dua tempat yang
               * akan berbeda pada suntingan berikutnya.
               */
              null
            ) : (
              <Button variant="secondary" onClick={() => void grant(row.purpose)} disabled={busy}>
                {t('consentSettings.grant')}
              </Button>
            )}
          </li>
        ))}
      </ul>

      <h2>{t('consentSettings.historyTitle')}</h2>
      {data.items.length === 0 ? (
        <EmptyState title={t('consentSettings.noneYet')} body={t('consentSettings.subtitle')} />
      ) : (
        <ul className="assist-consent-list">
          {data.items.map((row) => (
            <li className="assist-consent-list__item" key={row.id}>
              <strong>{t(`privacy.consent.${row.purpose}.label`, { organisation })}</strong>
              <span>
                {row.granted_at
                  ? t('consentSettings.granted', { date: formatDate(row.granted_at, locale) })
                  : t('consentSettings.withdrawn', {
                      date: formatDate(row.withdrawn_at ?? row.recorded_at, locale),
                    })}
              </span>
              {row.notice_version !== null ? (
                <span>{t('consentSettings.noticeVersion', { version: row.notice_version })}</span>
              ) : null}
            </li>
          ))}
        </ul>
      )}

      {confirmingRow ? (
        <Dialog
          kind="destructive"
          open
          title={t('consentSettings.confirmTitle')}
          description={t(`privacy.withdrawEffect.${confirmingRow.withdrawal_effect}`)}
          confirmLabel={t('consentSettings.withdraw')}
          cancelLabel={t('app.cancel')}
          onConfirm={() => void withdraw(confirmingRow.purpose)}
          onCancel={() => setConfirming(null)}
        />
      ) : null}
    </section>
  );
}

function formatDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
      new Date(iso),
    );
  } catch {
    return iso.slice(0, 16);
  }
}
